import { CommonModule } from '@angular/common';
import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { GenVideoPreviewData } from '../../../../../../../models/google-template/preview-interfaces/gen-video-preview-data.interface';
import { VideoFormat, YoutubeFormatHelper } from '../../../../../../../utils/youtube-format-helper';
import { DesktopFrameComponent } from '../../../preview-common/desktop-frame/desktop-frame.component';
import { YoutubeNavbarComponent } from '../../../preview-common/youtube/youtube-navbar/youtube-navbar.component';

/**
 * Layout for the Shorts ad on the desktop page: the vertical player in the
 * middle with the action rail beside it and the paging buttons at the page's
 * right edge.
 *
 * Presentational only, and not registered as a variant itself: the registered
 * per-format components wrap it and pass their own {@link format}.
 */
@Component({
    selector: 'app-gen-youtube-shorts-ad-desktop-shell',
    imports: [CommonModule, DesktopFrameComponent, YoutubeNavbarComponent],
    templateUrl: './youtube-shorts-ad-desktop-shell.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './youtube-shorts-ad-desktop-shell.component.scss'
})
export class YoutubeShortsAdDesktopShellComponent {
  @Input() data!: GenVideoPreviewData;

  /**
   * The creative format this placement previews, hardcoded by the wrapping
   * variant. The matching video is found by measured format rather than by
   * slot, so the author can paste the links in any order.
   */
  @Input() format: VideoFormat = '9:16';

  /** The guide's stubbed rows: three above the divider, six below it. */
  readonly guideTopRows = [0, 1, 2];
  readonly guideMainRows = [0, 1, 2, 3, 4, 5];

  /** 9:16 is the native Shorts frame; anything else needs the black bands. */
  get isFullscreen(): boolean {
    return this.format === '9:16';
  }

  /** Thumbnail of the video in this format, or '' when there isn't one yet. */
  get thumbnailUrl(): string {
    return YoutubeFormatHelper.thumbnailForFormat(this.videoIds, this.format);
  }

  /**
   * True while any link is still being measured, so the empty player says so
   * rather than asking for a video that is about to turn up.
   */
  get isResolving(): boolean {
    return YoutubeFormatHelper.isResolvingAny(this.videoIds);
  }

  private get videoIds(): string[] {
    return this.data.videos.map((video) => video.videoId);
  }
}
